// TAVEN Knowledge Hub — full academy course catalog
export const COURSE_CATEGORIES = [
  "Commerce & Marketplace", "Technology & AI", "Marketing & Growth", "Operations & Compliance",
];

export const LANGUAGES = ["English", "Français", "Kreyòl Ayisyen", "Español"];

const mod = (title, lessons) => ({ title, lessons });

export const COURSES = [
  {
    slug: "dropshipping", title: "Dropshipping Mastery", category: "Commerce & Marketplace",
    level: "Beginner", duration: "6 hrs", languages: ["English", "Français", "Kreyòl Ayisyen", "Español"],
    summary: "Launch a profitable dropshipping business inside the TAVEN ecosystem — from supplier selection to scaling.",
    objectives: ["Understand the dropshipping model and margins", "Find winning products and reliable suppliers", "Automate fulfillment and order flow", "Scale profitably while protecting cash flow"],
    modules: [
      mod("Foundations", ["What is dropshipping", "The TAVEN advantage", "Legal & tax basics"]),
      mod("Product & Supplier Research", ["Validating demand", "Vetting suppliers", "Sample & quality control"]),
      mod("Store & Fulfillment", ["Connecting your storefront", "Automated fulfillment", "Handling refunds"]),
      mod("Scaling", ["Ad economics", "Retention & LTV", "Building a brand"]),
    ],
  },
  {
    slug: "ecommerce-management", title: "E-Commerce Management", category: "Commerce & Marketplace",
    level: "Intermediate", duration: "8 hrs", languages: ["English", "Français", "Español"],
    summary: "Run a modern online store like an operator — analytics, merchandising, retention, and unit economics.",
    objectives: ["Master store KPIs and dashboards", "Merchandise for conversion", "Reduce CAC and grow LTV", "Build repeatable operating rhythms"],
    modules: [
      mod("Store Operations", ["Catalog structure", "Merchandising", "Site speed & UX"]),
      mod("Growth Metrics", ["Conversion rate", "AOV & LTV", "Cohort analysis"]),
      mod("Retention", ["Email & SMS", "Loyalty", "Post-purchase experience"]),
    ],
  },
  {
    slug: "supply-chain", title: "Supply Chain Management", category: "Operations & Compliance",
    level: "Intermediate", duration: "7 hrs", languages: ["English", "Français"],
    summary: "Design resilient supply chains: sourcing, logistics, inventory, and risk management for global commerce.",
    objectives: ["Map an end-to-end supply chain", "Optimize inventory levels", "Manage lead times & logistics", "Mitigate supplier risk"],
    modules: [
      mod("Sourcing", ["Supplier discovery", "Negotiation", "Contracts & terms"]),
      mod("Logistics", ["Freight & customs", "Last-mile delivery", "3PL selection"]),
      mod("Inventory", ["Forecasting", "Safety stock", "ABC analysis"]),
    ],
  },
  {
    slug: "artificial-intelligence", title: "Artificial Intelligence for Commerce", category: "Technology & AI",
    level: "Beginner", duration: "5 hrs", languages: ["English", "Español"],
    summary: "Use AI to research products, write copy, personalize offers, and automate support in your business.",
    objectives: ["Understand practical AI use-cases", "Automate content and product research", "Personalize the shopping experience", "Deploy AI responsibly"],
    modules: [
      mod("AI Fundamentals", ["How modern AI works", "Prompting basics", "Ethics & limits"]),
      mod("AI in the Store", ["Product research", "Copywriting", "Personalization"]),
      mod("Automation", ["AI support agents", "Workflow automation", "Measuring impact"]),
    ],
  },
  {
    slug: "technology", title: "Technology Essentials", category: "Technology & AI",
    level: "Beginner", duration: "4 hrs", languages: ["English", "Français", "Kreyòl Ayisyen"],
    summary: "The technology stack every modern seller should understand — from storefronts to data and security.",
    objectives: ["Understand core commerce technology", "Protect data & accounts", "Choose the right tools", "Speak the language of tech"],
    modules: [
      mod("The Stack", ["Storefronts & backends", "Payments & checkout", "Integrations"]),
      mod("Data & Security", ["Analytics basics", "Account security", "Privacy & compliance"]),
    ],
  },
  {
    slug: "marketplace-selling", title: "Marketplace Selling", category: "Commerce & Marketplace",
    level: "Intermediate", duration: "6 hrs", languages: ["English", "Français", "Español"],
    summary: "Win on marketplaces — listings, ratings, fulfillment standards, and multi-channel strategy.",
    objectives: ["Create high-converting listings", "Meet marketplace performance standards", "Manage ratings & reviews", "Expand across channels"],
    modules: [
      mod("Listings", ["Titles & images", "Attributes & SEO", "Pricing"]),
      mod("Performance", ["Fulfillment standards", "Ratings management", "Policy compliance"]),
    ],
  },
  {
    slug: "affiliate-marketing", title: "Affiliate Marketing", category: "Marketing & Growth",
    level: "Beginner", duration: "5 hrs", languages: ["English", "Français", "Kreyòl Ayisyen", "Español"],
    summary: "Promote products and build a network the right way — referral links, content, and ethical growth.",
    objectives: ["Understand affiliate economics", "Share the correct referral link", "Create promotional content", "Build and support a team"],
    modules: [
      mod("Getting Started", ["How affiliate income works", "Your referral vs store link", "Compliance & disclosures"]),
      mod("Promotion", ["Content that converts", "Social & video", "Tracking results"]),
      mod("Leadership", ["Recruiting ethically", "Supporting your team", "Rank advancement"]),
    ],
  },
  {
    slug: "product-research", title: "Product Research", category: "Commerce & Marketplace",
    level: "Beginner", duration: "4 hrs", languages: ["English", "Español"],
    summary: "Find winning products with data — demand validation, competition analysis, and margin math.",
    objectives: ["Validate product demand", "Analyze competition", "Calculate true margins", "Build a testing pipeline"],
    modules: [
      mod("Demand", ["Trend signals", "Keyword research", "Seasonality"]),
      mod("Validation", ["Competition analysis", "Margin math", "Test framework"]),
    ],
  },
  {
    slug: "customer-service", title: "Customer Service Excellence", category: "Operations & Compliance",
    level: "Beginner", duration: "4 hrs", languages: ["English", "Français", "Kreyòl Ayisyen"],
    summary: "Deliver world-class support that builds loyalty, protects reviews, and increases repeat purchases.",
    objectives: ["Handle inquiries professionally", "Resolve disputes calmly", "Turn issues into loyalty", "Meet response-time standards"],
    modules: [
      mod("Fundamentals", ["Tone & empathy", "Response standards", "Channels"]),
      mod("Resolution", ["Refunds & returns", "Escalation", "Turning detractors into fans"]),
    ],
  },
  {
    slug: "business-leadership", title: "Business Leadership", category: "Marketing & Growth",
    level: "Advanced", duration: "6 hrs", languages: ["English", "Français"],
    summary: "Lead teams and build a legacy — vision, culture, accountability, and sustainable growth.",
    objectives: ["Set vision and goals", "Build accountable teams", "Lead through change", "Create a lasting legacy"],
    modules: [
      mod("Vision", ["Mission & values", "Goal-setting", "Communication"]),
      mod("Teams", ["Hiring & culture", "Accountability", "Coaching"]),
    ],
  },
  {
    slug: "digital-marketing", title: "Digital Marketing", category: "Marketing & Growth",
    level: "Intermediate", duration: "7 hrs", languages: ["English", "Français", "Español"],
    summary: "Drive traffic and sales across paid, organic, email, and social channels with measurable ROI.",
    objectives: ["Build a channel strategy", "Run profitable ad campaigns", "Grow organic & email", "Measure ROI"],
    modules: [
      mod("Channels", ["Paid ads", "SEO & content", "Email & SMS", "Social"]),
      mod("Analytics", ["Attribution", "A/B testing", "Budget allocation"]),
    ],
  },
  {
    slug: "brand-building", title: "Brand Building", category: "Marketing & Growth",
    level: "Intermediate", duration: "5 hrs", languages: ["English", "Español"],
    summary: "Build a memorable brand — identity, voice, storytelling, and community that commands premium prices.",
    objectives: ["Define brand identity", "Craft a compelling story", "Build a consistent voice", "Grow a community"],
    modules: [
      mod("Identity", ["Positioning", "Visual identity", "Voice & tone"]),
      mod("Community", ["Storytelling", "Content strategy", "Loyalty"]),
    ],
  },
  {
    slug: "shopify-store-management", title: "Store Management", category: "Technology & AI",
    level: "Beginner", duration: "5 hrs", languages: ["English", "Français", "Kreyòl Ayisyen"],
    summary: "Operate a high-performance storefront — catalog, collections, navigation, and conversion tuning.",
    objectives: ["Organize catalog & collections", "Optimize navigation", "Improve conversion", "Maintain store health"],
    modules: [
      mod("Setup", ["Catalog & collections", "Navigation & menus", "Pages & policies"]),
      mod("Optimization", ["Conversion tuning", "Speed", "Health checks"]),
    ],
  },
  {
    slug: "inventory-management", title: "Inventory Management", category: "Operations & Compliance",
    level: "Intermediate", duration: "4 hrs", languages: ["English", "Français"],
    summary: "Keep the right stock at the right time — forecasting, reorder points, and avoiding dead stock.",
    objectives: ["Forecast demand", "Set reorder points", "Avoid stockouts & overstock", "Track inventory KPIs"],
    modules: [
      mod("Planning", ["Demand forecasting", "Reorder points", "Safety stock"]),
      mod("Control", ["Cycle counts", "Dead stock", "KPIs"]),
    ],
  },
  {
    slug: "pricing-profit", title: "Pricing & Profit", category: "Operations & Compliance",
    level: "Intermediate", duration: "4 hrs", languages: ["English", "Español"],
    summary: "Price for profit — cost structures, margins, psychological pricing, and protecting net profit.",
    objectives: ["Understand cost structures", "Set profitable prices", "Use pricing psychology", "Protect minimum net profit"],
    modules: [
      mod("Costs & Margins", ["Landed cost", "Contribution margin", "Break-even"]),
      mod("Strategy", ["Psychological pricing", "Discount strategy", "Profit guardrails"]),
    ],
  },
  {
    slug: "seller-compliance", title: "Seller Compliance", category: "Operations & Compliance",
    level: "Beginner", duration: "3 hrs", languages: ["English", "Français", "Kreyòl Ayisyen", "Español"],
    summary: "Stay compliant and protected — policies, restricted products, IP, and platform standards.",
    objectives: ["Understand platform policies", "Avoid restricted products", "Respect intellectual property", "Maintain account health"],
    modules: [
      mod("Policies", ["Marketplace rules", "Restricted products", "Truthful listings"]),
      mod("Protection", ["IP & trademarks", "Data & privacy", "Account health"]),
    ],
  },
];
